import { useState, useEffect } from "react";
import { getTickets, TicketStateHandlers } from "../api/get";

export interface UseTicketsConfig {
    endpoint: string;
    accessToken: string;
    memberPageUrl?: string;
    isMemberTicket?: boolean;
    onSuccess?: (data: any[], admissions: any[]) => void;
    onError?: (error: any) => void;
}

export interface UseTicketsReturn {
    ticketData: any[];
    admissions: any[];
    isLoading: boolean;
    error: any;
    setupError: boolean;
    ticketSoldOut: boolean;
    ticketExpired: boolean;
    ticketNotExists: boolean;
    memberExpired: boolean;
    memberInactive: boolean;
    memberPageUrl?: string;
}

export const useTickets = (
    config: UseTicketsConfig,
    stateSetters: {
        setSetupError: (error: boolean) => void;
        setTicketSoldOut: (soldOut: boolean) => void;
        setTicketExpired: (expired: boolean) => void;
        setTicketNotExists: (notExists: boolean) => void;
        setMemberExpired?: (expired: boolean) => void;
        setMemberInactive?: (inactive: boolean) => void;
        redirectToLoginPage: () => void;
    }
): UseTicketsReturn => {
    const [ticketData, setTicketData] = useState<any[]>([]);
    const [admissions, setAdmissions] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const [setupError, setSetupError] = useState(false);
    const [ticketSoldOut, setTicketSoldOut] = useState(false);
    const [ticketExpired, setTicketExpired] = useState(false);
    const [ticketNotExists, setTicketNotExists] = useState(false);
    const [memberExpired, setMemberExpired] = useState(false);
    const [memberInactive, setMemberInactive] = useState(false);

    useEffect(() => {
        const fetchTickets = async () => {
            try {
                const timeout = setTimeout(() => setIsLoading(true), 300);

                const stateHandlers: TicketStateHandlers = {
                    setSetupError: (error: boolean) => {
                        setSetupError(error);
                        stateSetters.setSetupError(error);
                    },
                    setTicketSoldOut: (soldOut: boolean) => {
                        setTicketSoldOut(soldOut);
                        stateSetters.setTicketSoldOut(soldOut);
                    },
                    setTicketExpired: (expired: boolean) => {
                        setTicketExpired(expired);
                        stateSetters.setTicketExpired(expired);
                    },
                    setTicketNotExists: (notExists: boolean) => {
                        if (config.isMemberTicket) {
                            // For member tickets, treat as setup error
                            setSetupError(true);
                            stateSetters.setSetupError(true);
                        } else {
                            setTicketNotExists(notExists);
                            stateSetters.setTicketNotExists(notExists);
                        }
                    },
                    redirectToLoginPage: stateSetters.redirectToLoginPage,
                    setMemberExpired: (expired: boolean) => {
                        setMemberExpired(expired);
                        stateSetters.setMemberExpired?.(expired);
                    },
                    setMemberInactive: (inactive: boolean) => {
                        setMemberInactive(inactive);
                        stateSetters.setMemberInactive?.(inactive);
                    },
                };

                const [data, admissionsData, , apiError] = await getTickets(
                    config.endpoint,
                    config.accessToken,
                    (error: boolean) => {
                        setSetupError(error);
                        stateSetters.setSetupError(error);
                    },
                    stateHandlers,
                    config.memberPageUrl
                );

                if (!apiError) {
                    setTicketData(data);
                    setAdmissions(admissionsData);
                    config.onSuccess?.(data, admissionsData);
                } else {
                    setError(apiError);
                    config.onError?.(apiError);
                }

                clearTimeout(timeout);
                setIsLoading(false);
            } catch (fetchError) {
                setError(fetchError);
                setSetupError(true);
                stateSetters.setSetupError(true);
                config.onError?.(fetchError);
                setIsLoading(false);
            }
        };

        fetchTickets();
    }, [config.endpoint, config.accessToken]);

    return {
        ticketData,
        admissions,
        isLoading,
        error,
        setupError,
        ticketSoldOut,
        ticketExpired,
        ticketNotExists,
        memberExpired,
        memberInactive,
        memberPageUrl: config.memberPageUrl,
    };
};
